import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* c8 ignore start */
import { useContext } from 'react';
import { Box, Button, ButtonContainer, useSnackbar } from '@emburse/embark-core';
import { useFormContext } from 'react-hook-form';
import dayjs from 'dayjs';
import { $t, useTranslation } from '../../../../hooks/useTranslation';
import { CreateExpenseReportContext } from '../../utils/CreateExpenseReportContext';
import { ALERT_TYPE, CREATE_EXPENSE_REPORT_OPTIONS } from '../../utils/constants';
import { createExpenseReport, getExpenseReportPreview, getExpenseReportCreationOptions } from '../../utils/api';
import { DEFAULT_DATE_FORMAT } from '../../utils/config';
import useLoadingOverlay from '../../../../hooks/useLoadingOverlay';
import { logger } from '../../../../util/logger';
function CreateExpenseReportButtonControls() {
    useTranslation();
    const form = useFormContext();
    const { enqueueSnackbar } = useSnackbar();
    const { applyLoadingState, removeLoadingState } = useLoadingOverlay();
    const { stepNumber, setStepNumber, selectedOptionValue, setAlertType, setSelectedOptionValue, setExpenseReportCreationOptions, setExpenseReportPreview, expenseReportCreationViewModel, skippedToBlankReport } = useContext(CreateExpenseReportContext);
    const onSubmitForm = async (data) => {
        try {
            applyLoadingState();
            const response = await createExpenseReport({
                Type: selectedOptionValue,
                Name: data.reportName,
                Description: data.description,
                StartDate: data.startDate?.toISOString(),
                EndDate: data.endDate?.toISOString(),
                Billable: data.billable,
                LinkedSpendRequestId: data.linkToSpendRequestId,
                UdfConfig: {
                    '1': data.udf1 ?? null,
                    '2': data.udf2 ?? null,
                    '3': data.udf3 ?? null,
                    '4': data.udf4 ?? null,
                    '5': data.udf5 ?? null
                },
                PCardIds: data?.cardPrograms?.map?.((card) => card.PCardId) ||
                    []
            });
            window.location.href = `ExpRptView.aspx?ID=${response.Id}`;
        }
        catch (e) {
            enqueueSnackbar($t(`Failed to create expense report: ${e.message}`), {
                variant: 'alert',
                AlertProps: { severity: 'error' }
            });
        }
        finally {
            removeLoadingState();
        }
    };
    const onClickBackButton = () => {
        if (stepNumber === 1) {
            window.location.href = 'Home.aspx';
            return;
        }
        if (stepNumber === 2) {
            // Reset all form values to their initial state
            setAlertType(null);
            form.reset();
            setStepNumber(1);
            setSelectedOptionValue(null);
        }
    };
    const handleCreateExpenseReportDateRange = async (startDate, endDate, type, formData) => {
        const { ExpectedExpenseCount, ExpectedReceiptCount } = await getExpenseReportPreview(selectedOptionValue, startDate, endDate, formData?.cardPrograms?.map?.((card) => card.PCardId.toString()) || []);
        if (type === CREATE_EXPENSE_REPORT_OPTIONS.CREDIT_CARD_TRANSACTIONS &&
            ExpectedExpenseCount === 0) {
            setAlertType(ALERT_TYPE.DATE_RANGE_EXPENSE_AND_REPORT_NUMBERS_ERROR);
            return;
        }
        if ((type === CREATE_EXPENSE_REPORT_OPTIONS.DATE_RANGE ||
            type === CREATE_EXPENSE_REPORT_OPTIONS.ALL_ITEMS) &&
            ExpectedExpenseCount === 0 &&
            ExpectedReceiptCount === 0) {
            setAlertType(ALERT_TYPE.DATE_RANGE_EXPENSE_AND_REPORT_NUMBERS_ERROR);
            return;
        }
        if (type === CREATE_EXPENSE_REPORT_OPTIONS.CREDIT_CARD_TRANSACTIONS) {
            let cardProgramName = expenseReportCreationViewModel.CardPrograms?.[0]?.CardProgramName;
            if (expenseReportCreationViewModel.CardPrograms?.length > 1) {
                cardProgramName = 'Credit Card Expenses';
            }
            form.setValue('reportName', `Expenses - ${cardProgramName} - ${dayjs(startDate).format(DEFAULT_DATE_FORMAT)} - ${dayjs(endDate).format(DEFAULT_DATE_FORMAT)}`);
            form.setValue('cardPrograms', formData?.cardPrograms ?? []);
            setAlertType(ALERT_TYPE.DATE_RANGE_EXPENSE_NUMBERS_INFO);
        }
        if (type === CREATE_EXPENSE_REPORT_OPTIONS.DATE_RANGE ||
            type === CREATE_EXPENSE_REPORT_OPTIONS.ALL_ITEMS) {
            form.setValue('reportName', `Expenses - ${dayjs(startDate).format(DEFAULT_DATE_FORMAT)} - ${dayjs(endDate).format(DEFAULT_DATE_FORMAT)}`);
            setAlertType(ALERT_TYPE.DATE_RANGE_EXPENSE_AND_REPORT_NUMBERS_INFO);
        }
        form.setValue('startDate', dayjs(startDate));
        form.setValue('endDate', dayjs(endDate));
        setExpenseReportPreview({
            ExpectedExpenseCount,
            ExpectedReceiptCount
        });
        setStepNumber(2);
    };
    const handleCreateExpenseReportAllItemsSelection = async () => {
        await handleCreateExpenseReportDateRange(expenseReportCreationViewModel.EarliestExpenseDate, expenseReportCreationViewModel.LatestExpenseDate, CREATE_EXPENSE_REPORT_OPTIONS.ALL_ITEMS);
    };
    const handleCreateExpenseReportCreditCardTransactionsSelection = async (formData) => {
        await handleCreateExpenseReportDateRange(formData.startDate, formData.endDate, CREATE_EXPENSE_REPORT_OPTIONS.CREDIT_CARD_TRANSACTIONS, formData);
    };
    const handleCreateExpenseReportDateRangeSelection = async (formData) => {
        await handleCreateExpenseReportDateRange(formData.startDate, formData.endDate, CREATE_EXPENSE_REPORT_OPTIONS.DATE_RANGE);
    };
    const handleCreateExpenseReportBlankSelection = () => {
        setAlertType(null);
        setStepNumber(2);
    };
    const handleStep1ToStep2 = async () => {
        if (selectedOptionValue === null) {
            setAlertType(ALERT_TYPE.EMPTY_OPTION_WARNING);
            return;
        }
        applyLoadingState();
        // if there are no errors, then the handleSubmit callback will be called
        await form.handleSubmit(async (formData) => {
            const retrievedExpenseReportCreationOptions = await getExpenseReportCreationOptions();
            setExpenseReportCreationOptions(retrievedExpenseReportCreationOptions);
            switch (selectedOptionValue) {
                case CREATE_EXPENSE_REPORT_OPTIONS.ALL_ITEMS:
                    await handleCreateExpenseReportAllItemsSelection();
                    break;
                case CREATE_EXPENSE_REPORT_OPTIONS.CREDIT_CARD_TRANSACTIONS:
                    await handleCreateExpenseReportCreditCardTransactionsSelection(formData);
                    break;
                case CREATE_EXPENSE_REPORT_OPTIONS.DATE_RANGE:
                    await handleCreateExpenseReportDateRangeSelection(formData);
                    break;
                case CREATE_EXPENSE_REPORT_OPTIONS.BLANK:
                    handleCreateExpenseReportBlankSelection();
                    break;
                default:
                    break;
            }
        }, (error) => {
            // Enable logging here to help debug form submission silent errors
            logger.error('form submission `handleStep1ToStep2` error:', JSON.stringify(error, null, 2));
            if (error.startDate || error.endDate) {
                setAlertType(ALERT_TYPE.DATE_RANGE_OPTION_ERROR);
            }
        })();
        removeLoadingState();
    };
    const onClickNextButton = async () => {
        switch (stepNumber) {
            case 1:
                await handleStep1ToStep2();
                break;
            case 2:
                await form.handleSubmit(onSubmitForm, (error) => {
                    // Enable logging here to help debug form submission silent errors
                    logger.error('form submission `onClickNextButton` error:', JSON.stringify(error, null, 2));
                })();
                break;
            default:
                break;
        }
    };
    return (_jsx(Box, { children: _jsxs(ButtonContainer, { children: [!skippedToBlankReport && (_jsx(Button, { variant: "outlined", onClick: onClickBackButton, children: $t('Back') })), _jsx(Button, { onClick: onClickNextButton, children: stepNumber === 1 ? $t('Next') : $t('Create Report') })] }) }));
}
export default CreateExpenseReportButtonControls;
